import { createSlice, createSelector } from '@reduxjs/toolkit';
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, initMapFactory, initMultiMapFactory } from '../common/reducers/utilities';
import { ensureArray } from '../common/utils/arrayUtilities';
import { createEagerSelector, eagerMetaFactory } from '../common/reducers/pendingUpdateSlice';
import { aggregatingFetch } from '../common/hooks/networkHook';
import { deepEquals } from '../common/utils/objectUtilities';

const compareListDefs = (d1, d2) => d1.id === d2.id;
const compareListItems = (i1, i2) => i1.id === i2.id;

const listSlice = createSlice({
    name: 'lists',
    initialState: {
        defs: [],
        data: {},
        detailedData: {}
    },
    reducers: {
        setDefs: (state, action) => {
            if (!deepEquals(state.defs, action.payload)) {
                state.defs = action.payload
            }
        },
        setListData: (state, action) => {
            action.payload.listids.forEach(id => {
                delete state.data[id];
            });
            action.payload.data.forEach(e => {
                if (!state.data[e.listid]) {
                    state.data[e.listid] = [];
                }
                state.data[e.listid].push(e);
            })
        },
        setDetailedListData: (state, action) => {
            ensureArray(action.payload).forEach(e => state.detailedData[e.id] = e)
        },
        addOrUpdateDef: (state, action) => {
            for (const d of ensureArray(action.payload)) {
                let idx = state.defs.findIndex(d2 => compareListDefs(d, d2));
                if (idx >= 0) {
                    state.defs.splice(idx, 1, d);
                } else {
                    state.defs.push(d);
                }
            }
        },
        addOrUpdateEntry: (state, action) => {
            for (const e of ensureArray(action.payload).map(e => e.status ? e.data : e)) {
                if (!state.data[e.listid]) {
                    state.data[e.listid] = [];
                }
                let idx = state.data[e.listid].findIndex(e2 => compareListItems(e, e2));
                if (idx >= 0) {
                    state.data[e.listid].splice(idx, 1, e);
                } else {
                    state.data[e.listid].push(e);
                }
            }
        },
        addOrUpdateDetailedEntry: (state, action) => {
            for (const e of ensureArray(action.payload).map(e => e.status ? e.data : e)) {
                state.detailedData[e.id] = e;
            }
        },
        removeDef: (state, action) => {
            for (const d of ensureArray(action.payload)) {
                let idx = state.defs.findIndex(d2 => compareListDefs(d2, d));
                if (idx >= 0) {
                    state.defs.splice(idx, 1);
                }
            }
        },
        removeEntry: (state, action) => {
            for (const d of ensureArray(action.payload)) {
                if (state.data[d.listid]) {
                    let idx = state.data[d.listid].findIndex(e => compareListItems(d, e));
                    if (idx >= 0) {
                        state.data[d.listid].splice(idx, 1);
                    }
                }
                delete state.detailedData[d.id];
            }
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => listSlice.getInitialState());
    }
});

// #region network operations
const fetchListDefs = network => network.get('/api/v1/lists', listSlice.actions.setDefs);
const fetchListData = aggregatingFetch(
    listid => network => network.get('/api/v1/listdata/' + encodeURIComponent(listid), data => listSlice.actions.setListData({listids: [listid], data})),
    listids => network => network.post('/api/v1/listdataForLists', listids, data => listSlice.actions.setListData({listids, data}))
);
const fetchListDatum = id => network => network.get('/api/v1/listdatum/' + encodeURIComponent(id), listSlice.actions.addOrUpdateDetailedEntry);

const eagerMeta = eagerMetaFactory(listSlice.name);
export const createListEntry = (network, entry) => network.post('/api/v1/listdata', entry, listSlice.actions.addOrUpdateEntry, eagerMeta(entry.id));
export const updateListEntry = (network, entry) => network.put('/api/v1/listdata', entry, listSlice.actions.addOrUpdateDetailedEntry, eagerMeta(entry.id));
export const removeListEntry = (network, entry) => network.delete('/api/v1/listdata/' + encodeURIComponent(entry.id), listSlice.actions.removeEntry, eagerMeta(entry.id));

export const createListDef = (network, list) => network.post('/api/v1/list', list, listSlice.actions.addOrUpdateDef);
export const updateListDef = (network, list) => network.put('/api/v1/list', list, listSlice.actions.addOrUpdateDef);
export const removeListDef = (network, list) => network.delete('/api/v1/list/' + encodeURIComponent(list), listSlice.actions.removeDef);
// #endregion

// #region permission checks
const canAccessList = (credentials, list, write) => {
    if (!list) {
        return false;
    }
    if (credentials.access[write ? 'hasFull' : 'has'](['administrator', 'dispatcher'])) {
        return true;
    }
    return (!list.writetypes || credentials.access[write ? 'hasFull' : 'has'](list.writetypes))
        || (!write && (!list.readtypes || credentials.access.has(list.readtypes)));
}
export const canUpdateList = (credentials, list) => canAccessList(credentials, list, true);
export const canCreateList = (credentials, list) => canAccessList(credentials, list, true);
export const canRemoveList = (credentials, list) => canAccessList(credentials, list, true);
export const canViewList = (credentials, list) => canAccessList(credentials, list, false);

export const canUpdateListDef = (credentials, list) => credentials.access.hasFull('administrator');
export const canCreateListDef = (credentials, list) => credentials.access.hasFull('administrator');
export const canRemoveListDef = (credentials, list) => credentials.access.hasFull('administrator');
// #endregion

// #region selectors
const selectListDefs = state => state.lists.defs;
const selectAll = state => state.lists;

// we now make a reselect to memoize a select function parameterized by jobid and expose that factory through a convenience function
const selectDefById = createSelector([selectListDefs, (defs, id) => id], (defs, id) => defs.find(d => d.id === id));
const selectListDef = id => state => selectDefById(state, id);

const eagerSelector = createEagerSelector(listSlice.name, { mode: 'array'});
const selectDataForList = eagerSelector(createSelector([selectAll, (lists, listid) => listid], (lists, listid) => {
    const entries = Array.from(lists.data[listid] ?? []);
    Object.values(lists.detailedData).filter(e => e.listid === listid).forEach(e => {
        const idx = entries.findIndex(e2 => e2.id === e.id);
        if (idx > -1) {
            entries.splice(idx, 1, e);
        } else {
            entries.push(e);
        }
    })
    return entries;
}));
const selectListData = listid => state => selectDataForList(state, listid);

const selectListDataForLists = listids => state => listids.flatMap(listid => selectDataForList(state, listid));

const selectDataById = eagerSelector(createSelector([selectAll, (lists, id) => id], (lists, id) => {
    return [lists.detailedData[id] ?? Object.values(lists.data).flat().find(d => d.id === id)];
}));
const selectListDatum = id => state => selectDataById(state, id)[0];
// #endregion

// #region listeners
const listen = {
    listCreated: (entry, { dispatch }) => dispatch(listSlice.actions.addOrUpdateDef(entry)),
    listUpdated: (entry, { dispatch }) => dispatch(listSlice.actions.addOrUpdateDef(entry)),
    listRemoved: (entry, { dispatch }) => dispatch(listSlice.actions.removeDef(entry)),
    listEntryCreated: (entry, { dispatch }) => dispatch(listSlice.actions.addOrUpdateDetailedEntry(entry)),
    listEntryUpdated: (entry, { dispatch }) => dispatch(listSlice.actions.addOrUpdateDetailedEntry(entry)),
    listEntryRemoved: (entry, { dispatch }) => dispatch(listSlice.actions.removeEntry(entry))
}
// #endregion

export const [useListDefs] = dataHookFactory(selectListDefs, { run: fetchListDefs, listen });
export const [useListDef, realizeListDef] = dataHookFactory(selectListDef, listid => ({ run: fetchListDefs, listen }));

const initListData = initMapFactory(fetchListData, listen);
export const [useListData] = dataHookFactory(selectListData, initListData);
export const [useListDataForLists] = dataHookFactory(selectListDataForLists, initMultiMapFactory(initListData, listen));
const initListDatum = initMapFactory(fetchListDatum, listen);
export const [useListDatum, realizeListDatum] = dataHookFactory(selectListDatum, initListDatum);

const reducerDetails = { name: listSlice.name, reducer: listSlice.reducer };
export default reducerDetails;